import { useEffect, useState } from 'react';

import { useWindowEvent } from './useWindowEvent';

/**
 * Tracks whether the page is scrolled at or past `threshold` pixels — the
 * "sticky/slim header" trigger used by the nav bars and static pages.
 *
 * @param {number|Function} threshold pixel offset, or a function returning one
 *   for thresholds that depend on a rendered element (e.g. a ref's height).
 *   A nullish value leaves the current state untouched, so it is safe to read
 *   from a ref that is not populated yet.
 * @returns {boolean}
 */
export const useScrolledPast = (threshold) => {
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  const readScrollPosition = () => {
    const offset = typeof threshold === 'function' ? threshold() : threshold;

    if (offset == null) return;

    setIsScrolledPast(window.scrollY >= offset);
  };

  useWindowEvent('scroll', readScrollPosition, { passive: true });

  // The page can already be scrolled when we mount — a refresh part-way down,
  // or a route change that keeps the offset — and no scroll event follows that.
  // Only when actually scrolled: at the top the answer is already `false`, and
  // a ref-based threshold can still measure 0 before layout settles.
  useEffect(() => {
    if (window.scrollY > 0) readScrollPosition();
  }, []);

  return isScrolledPast;
};
